const SDK_VERSION = '1.1.1-user-center-server-identity'
const VISITOR_KEY = 'translate_track_visitor_id'
const SESSION_KEY = 'translate_track_session_id'
const SESSION_STARTED_KEY = `${SESSION_KEY}_started`
const VISITOR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const MAX_QUEUE_SIZE = 20
const MAX_RETRY_QUEUE_SIZE = 200
const FLUSH_INTERVAL = 5000
const INTERACTION_DEDUPE_MS = 450
const DEFAULT_ENDPOINT = 'https://track.wesitrans.com/track'

const UI_TRACKABLE_CLASSES = [
  'el-button',
  'el-menu-item',
  'el-dropdown-menu__item',
  'el-tabs__item',
  'el-select-dropdown__item',
  'el-checkbox',
  'el-radio',
  'el-switch',
  'el-upload',
  'el-pagination'
]

const TRACKABLE_CLASS_KEYWORDS = [
  'btn', 'button', 'link', 'tab', 'menu', 'option', 'select', 'switch',
  'checkbox', 'radio', 'close', 'download', 'submit', 'confirm', 'cancel',
  'delete', 'update', 'back', 'next', 'prev', 'payment', 'recharge', 'action',
  'select-frame', 'timebutton', 'success', 'no-1', 'no2-1'
]

const TRACKABLE_ROLES = ['button', 'link', 'menuitem', 'tab', 'checkbox', 'radio', 'switch', 'option']
const IGNORE_CLASS_KEYWORDS = ['layui-layer-shade', 'modal-mask', 'overlay', 'backdrop']
const SENSITIVE_KEY_PATTERN = /password|passwd|token|secret|mobile|phone|email|idcard|identity|captcha|validate|content|input|query|requestbody|responsebody|ordertext|originaltext/i
const SAFE_PROPERTY_KEY_PATTERN = /^(action|result|reason|source|mode|plan|amount|count|selectedCount|pageNumber|pageSize|fileCount|fileExtensions|fileSizeBucket|fieldType|checked|validationPassed|provider|channel|status|businessCode|route|target|operation|trigger|isNewSession|durationMs|maxScrollDepth)$/

let options = {}
let queue = []
let timer = null
let identityTimer = null
let currentRoute = null
let pageEnterTime = Date.now()
let maxScrollDepth = 0
let inMemoryVisitorId = ''
let inMemorySessionId = ''
let lastInteractionSignature = ''
let lastInteractionTime = 0
let lastDownloadSignature = ''
let lastDownloadTime = 0
let lastIdentitySignature = ''
let initialized = false
const inputActivityTimes = typeof WeakMap !== 'undefined' ? new WeakMap() : null

function silently(work, fallback) {
  try {
    return work()
  } catch (error) {
    return fallback
  }
}

function safeLocalGet(key) {
  return silently(() => window.localStorage.getItem(key), '') || ''
}

function safeLocalSet(key, value) {
  silently(() => window.localStorage.setItem(key, value))
}

function sharedCookieGet(name) {
  return silently(() => {
    if (!/(^|\.)wesitrans\.com$/i.test(window.location.hostname)) return ''
    const prefix = `${encodeURIComponent(name)}=`
    const part = document.cookie.split(';').map(item => item.trim()).find(item => item.indexOf(prefix) === 0)
    return part ? decodeURIComponent(part.slice(prefix.length)) : ''
  }, '')
}

function sharedCookieSet(name, value) {
  silently(() => {
    if (!/(^|\.)wesitrans\.com$/i.test(window.location.hostname)) return
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${VISITOR_COOKIE_MAX_AGE}; Path=/; Domain=.wesitrans.com; SameSite=Lax; Secure`
  })
}

function safeSessionGet(key) {
  return silently(() => window.sessionStorage.getItem(key), '') || ''
}

function safeSessionSet(key, value) {
  silently(() => window.sessionStorage.setItem(key, value))
}

function getAuthToken() {
  const token = silently(() => {
    if (typeof options.getToken === 'function') return options.getToken()
    return ''
  }, '') || safeLocalGet('token') || safeSessionGet('token') || sharedCookieGet('token')
  return String(token || '').trim()
}

function authHeaders(token) {
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = token
  return headers
}

function safeWindowListener(name, listener, listenerOptions) {
  silently(() => window.addEventListener(name, listener, listenerOptions))
}

function safeDocumentListener(name, listener, listenerOptions) {
  silently(() => document.addEventListener(name, listener, listenerOptions))
}

export function setupTrack(Vue, trackOptions) {
  return silently(() => {
    if (initialized) return true
    options = Object.assign({
      sdkVersion: SDK_VERSION,
      endpoint: DEFAULT_ENDPOINT,
      flushOnInteraction: true
    }, trackOptions || {})
    if (!options.appId) return false

    initialized = true
    ensureVisitorId()
    ensureSessionId()
    patchClickListener()
    bindRouter(options.router)
    bindInteractions()
    bindDownloadTracking()
    bindLifecycle()
    bindErrors(Vue)
    bindPerformance()

    timer = silently(() => window.setInterval(() => {
      syncTrackIdentity()
      flush()
    }, FLUSH_INTERVAL), null)
    identityTimer = silently(() => window.setTimeout(() => syncTrackIdentity(), 0), null)

    track('session_start', { isNewSession: !safeSessionGet(SESSION_STARTED_KEY) }, 'session_start')
    safeSessionSet(SESSION_STARTED_KEY, '1')
    return true
  }, false)
}

export function track(eventName, properties = {}, eventType = 'custom_event') {
  silently(() => {
    if (!options.appId) return
    enqueue({
      eventType: safeIdentifier(eventType, 'custom_event'),
      eventName: safeIdentifier(eventName, 'custom_event'),
      page: pageInfo(currentRoute),
      properties: sanitizeProperties(properties)
    })
  })
}

export function trackAction(eventName, properties = {}, eventType = 'business_event') {
  track(eventName, properties, eventType)
}

export function trackApiRequest(config, status, businessCode, durationMs, success) {
  silently(() => {
    const url = String(config && config.url || '')
    if (!options.appId || isTrackRequest(url)) return
    const safeDuration = Math.max(0, Number(durationMs) || 0)
    enqueue({
      eventType: 'api_request',
      eventName: 'api_request',
      page: pageInfo(currentRoute),
      api: {
        path: safeApiPath(url),
        method: String(config && config.method || 'GET').toUpperCase().slice(0, 12),
        httpStatus: Number(status) || 0,
        businessCode: safeBusinessCode(businessCode),
        durationMs: safeDuration,
        success: Boolean(success)
      },
      metrics: { durationMs: safeDuration }
    })
  })
}

export function extractBusinessCode(data) {
  return silently(() => {
    if (data == null) return ''
    if (typeof data === 'boolean') return String(data)
    if (typeof data === 'number') {
      const text = String(data)
      return /^(?:0|1|[2-5]\d{2})$/.test(text) ? text : ''
    }
    if (typeof data === 'string') {
      const text = data.trim()
      return /^(?:0|1|[2-5]\d{2}|ok|success|true|false|need_login|unauthorized|forbidden|error|failed|failure)$/i.test(text) ? text : ''
    }
    return safeBusinessCode(first(data.code, data.status, data.businessCode, data.resultCode))
  }, '')
}

export function syncTrackIdentity() {
  silently(() => {
    if (!options.appId) return
    trackIdentify()
  })
}

export function trackIdentify() {
  silently(() => {
    const authToken = getAuthToken()
    const signature = `${hash(authToken || 'cookie')}:${ensureSessionId()}`
    if (signature === lastIdentitySignature) return
    lastIdentitySignature = signature
    const payload = {
      appId: options.appId,
      visitorId: ensureVisitorId(),
      sessionId: ensureSessionId(),
      bindTime: new Date().toISOString()
    }
    track('identity_bind', { result: 'bound' }, 'identity_bind')
    post('/identity/bind', payload)
  })
}

export function hashBusinessId(value) {
  const text = String(value == null ? '' : value)
  return text ? hash(text).slice(0, 16) : ''
}

export function flush(useBeacon = false) {
  silently(() => {
    if (!options.appId || queue.length === 0) return
    const events = queue.splice(0, queue.length)
    let payload = ''
    try {
      payload = JSON.stringify({
        appId: options.appId,
        sdkVersion: options.sdkVersion || SDK_VERSION,
        visitorId: ensureVisitorId(),
        sessionId: ensureSessionId(),
        events
      })
    } catch (error) {
      restoreQueue(events)
      return
    }

    const url = `${trimEnd(options.endpoint || DEFAULT_ENDPOINT)}/events/batch`
    const authToken = getAuthToken()
    if (useBeacon && !authToken && silently(() => Boolean(navigator.sendBeacon), false)) {
      const delivered = silently(() => navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' })), false)
      if (delivered) return
    }

    sendJson(url, payload, {
      keepalive: Boolean(useBeacon),
      onFailure: () => restoreQueue(events)
    })
  })
}

function enqueue(event) {
  silently(() => {
    queue.push(Object.assign({
      eventId: uuid(),
      clientTime: new Date().toISOString(),
      visitorId: ensureVisitorId(),
      sessionId: ensureSessionId(),
      screenWidth: silently(() => window.screen.width, 0) || 0,
      screenHeight: silently(() => window.screen.height, 0) || 0,
      viewportWidth: silently(() => window.innerWidth, 0) || 0,
      viewportHeight: silently(() => window.innerHeight, 0) || 0,
      networkType: silently(() => navigator.connection.effectiveType, '') || ''
    }, event))
    if (queue.length >= MAX_QUEUE_SIZE) flush()
  })
}

function restoreQueue(events) {
  silently(() => {
    queue = events.concat(queue).slice(0, MAX_RETRY_QUEUE_SIZE)
  })
}

function bindRouter(router) {
  silently(() => {
    if (!router || !router.afterEach) return
    currentRoute = router.currentRoute || null
    router.afterEach((to, from) => silently(() => {
      if (currentRoute) trackPageLeave(from || currentRoute)
      currentRoute = to
      pageEnterTime = Date.now()
      maxScrollDepth = 0
      enqueue({ eventType: 'page_view', eventName: 'page_view', page: pageInfo(to) })
      syncTrackIdentity()
    }))
  })
}

function trackPageLeave(route) {
  silently(() => {
    const durationMs = Math.max(0, Date.now() - pageEnterTime)
    enqueue({
      eventType: 'page_leave',
      eventName: 'page_leave',
      page: pageInfo(route),
      metrics: { durationMs, maxScrollDepth },
      durationMs
    })
  })
}

function bindInteractions() {
  ;['click', 'pointerup', 'mouseup'].forEach(name => {
    safeDocumentListener(name, event => recordElementInteraction(event, 'element_click', name), true)
  })
  safeDocumentListener('input', event => recordFieldSignal(event, 'input_activity', 'input'), true)
  safeDocumentListener('change', event => recordChange(event), true)
  safeDocumentListener('focusin', event => recordFocus(event), true)
  safeDocumentListener('focusout', event => recordFieldSignal(event, 'input_blur', 'blur'), true)
  safeDocumentListener('invalid', event => recordFieldSignal(event, 'input_invalid', 'invalid'), true)
  safeDocumentListener('submit', event => recordSubmit(event), true)
  safeDocumentListener('reset', event => recordFormReset(event), true)
  ;['copy', 'cut', 'paste'].forEach(name => {
    safeDocumentListener(name, event => recordFieldSignal(event, `clipboard_${name}`, name), true)
  })
  safeDocumentListener('drop', event => recordDrop(event), true)
  safeDocumentListener('keydown', event => {
    const key = String(event && event.key || '')
    if (key !== 'Enter' && key !== 'Escape' && key !== ' ') return
    recordFieldSignal(event, 'keyboard_action', key === ' ' ? 'Space' : key)
  }, true)
}

function recordFieldSignal(event, eventType, action) {
  silently(() => {
    const element = event && event.target
    if (!isFormControl(element) && !Boolean(element && element.isContentEditable)) return
    if (eventType === 'input_activity' && shouldThrottleInput(element)) return
    const meta = elementMeta(element)
    const signature = `${eventType}|${meta.trackId}|${action || ''}`
    if (eventType !== 'input_activity' && shouldSkipInteraction(signature)) return
    enqueue({
      eventType,
      eventName: eventType,
      page: pageInfo(currentRoute),
      element: meta,
      properties: {
        action: String(action || eventType).slice(0, 40),
        fieldType: safeFieldType(element),
        trigger: String(event && event.type || '').slice(0, 30)
      }
    })
  })
}

function shouldThrottleInput(element) {
  if (!inputActivityTimes || !element) return false
  const current = Date.now()
  const previous = inputActivityTimes.get(element) || 0
  if (current - previous < 1200) return true
  inputActivityTimes.set(element, current)
  return false
}

function recordElementInteraction(event, eventType, trigger) {
  silently(() => {
    if (event.button && event.button !== 0) return
    const match = closestTrackable(event.target)
    if (!match) return
    const element = match.element
    const meta = elementMeta(element)
    if (shouldSkipInteraction(`${eventType}|${meta.trackId}`)) return
    enqueue({
      eventType,
      eventName: meta.eventName || eventType,
      page: pageInfo(currentRoute),
      element: Object.assign({}, meta, {
        clickX: Math.round(event.clientX || 0),
        clickY: Math.round(event.clientY || 0)
      }),
      properties: { action: 'activate', trigger, source: match.source }
    })
    flushInteraction()
  })
}

function recordFocus(event) {
  silently(() => {
    const element = event.target
    if (!isFormControl(element)) return
    const meta = elementMeta(element)
    if (shouldSkipInteraction(`focus|${meta.trackId}`)) return
    enqueue({
      eventType: 'input_focus',
      eventName: 'input_focus',
      page: pageInfo(currentRoute),
      element: meta,
      properties: { fieldType: safeFieldType(element), action: 'focus' }
    })
  })
}

function recordChange(event) {
  silently(() => {
    const element = event.target
    if (!isFormControl(element)) return
    if (String(element.type || '').toLowerCase() === 'file') {
      recordUploadSelection(element)
      return
    }
    const meta = elementMeta(element)
    enqueue({
      eventType: 'element_change',
      eventName: 'element_change',
      page: pageInfo(currentRoute),
      element: meta,
      properties: compactObject({
        action: 'change',
        fieldType: safeFieldType(element),
        checked: /checkbox|radio/i.test(element.type || '') ? Boolean(element.checked) : undefined
      })
    })
  })
}

function recordSubmit(event) {
  silently(() => {
    const form = event.target
    if (!form || String(form.tagName || '').toLowerCase() !== 'form') return
    const meta = elementMeta(form)
    enqueue({
      eventType: 'form_submit',
      eventName: 'form_submit',
      page: pageInfo(currentRoute),
      element: meta,
      properties: { action: 'submit' }
    })
    flushInteraction()
  })
}

function recordFormReset(event) {
  silently(() => {
    const form = event && event.target
    if (!form || String(form.tagName || '').toLowerCase() !== 'form') return
    enqueue({
      eventType: 'form_reset',
      eventName: 'form_reset',
      page: pageInfo(currentRoute),
      element: elementMeta(form),
      properties: { action: 'reset' }
    })
    flushInteraction()
  })
}

function recordDrop(event) {
  silently(() => {
    const target = event && event.target
    const files = Array.prototype.slice.call(event && event.dataTransfer && event.dataTransfer.files || [])
    const extensions = files.map(file => fileExtension(file.name)).filter(Boolean)
    const totalBytes = files.reduce((sum, file) => sum + (Number(file.size) || 0), 0)
    enqueue({
      eventType: 'content_drop',
      eventName: 'content_drop',
      page: pageInfo(currentRoute),
      element: elementMeta(target),
      properties: {
        action: 'drop',
        fileCount: files.length,
        fileExtensions: unique(extensions).join(','),
        fileSizeBucket: sizeBucket(totalBytes)
      }
    })
  })
}

function recordUploadSelection(input) {
  silently(() => {
    const files = Array.prototype.slice.call(input.files || [])
    const extensions = files.map(file => fileExtension(file.name)).filter(Boolean)
    const totalBytes = files.reduce((sum, file) => sum + (Number(file.size) || 0), 0)
    enqueue({
      eventType: 'upload_select',
      eventName: 'upload_select',
      page: pageInfo(currentRoute),
      element: elementMeta(input),
      properties: {
        action: 'select_file',
        fileCount: files.length,
        fileExtensions: unique(extensions).join(','),
        fileSizeBucket: sizeBucket(totalBytes)
      }
    })
    flushInteraction()
  })
}

function bindDownloadTracking() {
  silently(() => {
    if (!window.HTMLAnchorElement || window.__translateUserTrackDownloadPatched) return
    window.__translateUserTrackDownloadPatched = true
    const nativeClick = window.HTMLAnchorElement.prototype.click
    window.HTMLAnchorElement.prototype.click = function () {
      silently(() => recordDownloadTrigger(this, 'programmatic_click'))
      return nativeClick.apply(this, arguments)
    }
    safeDocumentListener('click', event => {
      const link = event.target && event.target.closest ? event.target.closest('a') : null
      if (link) recordDownloadTrigger(link, 'user_click')
    }, true)
  })
}

function recordDownloadTrigger(link, trigger) {
  silently(() => {
    const downloadName = link.download || link.getAttribute('download') || ''
    const href = link.getAttribute('href') || link.href || ''
    const isDownload = Boolean(downloadName) || /^blob:|^data:/i.test(href) || /download/i.test(href)
    if (!isDownload) return
    const signature = `${fileExtension(downloadName)}|${safeApiPath(href)}|${trigger}`
    const now = Date.now()
    if (signature === lastDownloadSignature && now - lastDownloadTime < 1000) return
    lastDownloadSignature = signature
    lastDownloadTime = now
    track('download_trigger', {
      action: 'download',
      trigger,
      fileExtensions: fileExtension(downloadName)
    }, 'download')
  })
}

function bindLifecycle() {
  safeWindowListener('scroll', () => silently(() => {
    const doc = document.documentElement
    const max = Math.max(1, doc.scrollHeight - window.innerHeight)
    const depth = Math.min(100, Math.round(((window.scrollY || doc.scrollTop || 0) / max) * 100))
    maxScrollDepth = Math.max(maxScrollDepth, depth)
  }), { passive: true })
  safeDocumentListener('visibilitychange', () => silently(() => {
    if (document.visibilityState === 'hidden') {
      trackPageLeave(currentRoute)
      flush(true)
    }
  }))
  safeWindowListener('beforeunload', () => silently(() => {
    track('session_end', { durationMs: Math.max(0, Date.now() - pageEnterTime) }, 'session_end')
    flush(true)
    if (timer) window.clearInterval(timer)
    if (identityTimer) window.clearTimeout(identityTimer)
  }))
}

function bindErrors(Vue) {
  safeWindowListener('error', event => silently(() => {
    const target = event.target
    const isResource = target && target !== window && Boolean(target.src || target.href)
    trackError(isResource ? 'resource_error' : 'js_error', event.message || 'resource load error', event.error && event.error.stack || '')
  }), true)
  safeWindowListener('unhandledrejection', event => silently(() => {
    const reason = event.reason || {}
    trackError('promise_rejection', reason.message || String(reason), reason.stack || '')
  }))
  silently(() => {
    if (!Vue || !Vue.config) return
    const oldHandler = Vue.config.errorHandler
    Vue.config.errorHandler = (error, vm, info) => {
      silently(() => trackError('vue_error', error && error.message || String(error), error && error.stack || info))
      if (oldHandler) silently(() => oldHandler(error, vm, info))
    }
  })
}

function bindPerformance() {
  safeWindowListener('load', () => silently(() => {
    window.setTimeout(() => silently(() => {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0]
      if (!nav) return
      enqueue({
        eventType: 'performance',
        eventName: 'navigation_timing',
        page: pageInfo(currentRoute),
        performance: {
          ttfbMs: Math.round(nav.responseStart || 0),
          domReadyMs: Math.round(nav.domContentLoadedEventEnd || 0),
          loadMs: Math.round(nav.loadEventEnd || 0)
        }
      })
    }), 0)
  }))
}

function trackError(type, message, stack) {
  silently(() => {
    const safeMessage = scrubErrorText(message).slice(0, 180)
    const safeStack = scrubErrorText(String(stack || '').replace(/https?:\/\/[^\s)]+/g, '[url]')).slice(0, 1500)
    enqueue({
      eventType: 'frontend_error',
      eventName: safeIdentifier(type, 'frontend_error'),
      page: pageInfo(currentRoute),
      error: {
        type: safeIdentifier(type, 'frontend_error'),
        message: safeMessage,
        stack: safeStack,
        fingerprint: hash(`${type}|${safeMessage}|${safeStack}`)
      }
    })
    flush()
  })
}

function scrubErrorText(value) {
  return String(value || '')
    .replace(/[?&][^\s]*/g, '')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\b1\d{10}\b/g, '[phone]')
    .replace(/\b\d{6,}\b/g, '[number]')
    .replace(/\b(?:bearer|token|password|passwd|captcha)\s*[:=]?\s*[^\s,;]+/gi, '[credential]')
}

function patchClickListener() {
  silently(() => {
    if (!window.EventTarget || window.__translateUserTrackClickListenerPatched) return
    const rawAddEventListener = window.EventTarget.prototype.addEventListener
    Object.defineProperty(window, '__translateUserTrackClickListenerPatched', {
      value: true,
      configurable: false
    })
    window.EventTarget.prototype.addEventListener = function (type, listener, eventOptions) {
      silently(() => {
        if (type === 'click' && this && this.nodeType === 1 && this.setAttribute) {
          this.setAttribute('data-track-click-bound', '1')
        }
      })
      return rawAddEventListener.call(this, type, listener, eventOptions)
    }
  })
}

function closestTrackable(target) {
  return silently(() => {
    let node = target
    let fallback = null
    while (node && node !== document.body) {
      if (node.dataset && node.dataset.trackIgnore != null) return null
      if (hasIgnoredClass(node)) return null
      const source = strongTrackSource(node)
      if (source) return { element: node, source }
      if (!fallback && fallbackTrackSource(node)) fallback = { element: node, source: 'class_or_cursor' }
      node = node.parentElement
    }
    return fallback
  }, null)
}

function strongTrackSource(node) {
  if (!node || !node.tagName) return ''
  const tag = String(node.tagName).toLowerCase()
  const role = String(node.getAttribute && node.getAttribute('role') || '').toLowerCase()
  if (node.dataset && (node.dataset.trackId || node.dataset.trackText)) return 'data_track'
  if (tag === 'button' || tag === 'a') return `native_${tag}`
  if (tag === 'input' && /^(button|submit|reset|checkbox|radio)$/i.test(node.type || '')) return 'native_input'
  if (TRACKABLE_ROLES.indexOf(role) >= 0) return `role_${role}`
  if (hasAnyClass(node, UI_TRACKABLE_CLASSES)) return 'ui_component'
  if (node.dataset && node.dataset.trackClickBound) return 'event_listener'
  return ''
}

function fallbackTrackSource(node) {
  if (!node || !node.tagName || !node.getAttribute) return ''
  const tabindex = node.getAttribute('tabindex')
  if (tabindex != null && tabindex !== '-1') return 'tabindex'
  const classes = elementClass(node).toLowerCase()
  if (TRACKABLE_CLASS_KEYWORDS.some(keyword => classes.indexOf(keyword) >= 0)) return 'class_keyword'
  return silently(() => window.getComputedStyle(node).cursor === 'pointer', false) ? 'cursor_pointer' : ''
}

function elementMeta(element) {
  return silently(() => {
    const dataset = element.dataset || {}
    return {
      trackId: String(dataset.trackId || buildElementId(element)).slice(0, 160),
      text: safeElementText(element),
      type: String(element.getAttribute('role') || element.getAttribute('type') || '').slice(0, 40),
      tag: String(element.tagName || '').toLowerCase().slice(0, 24),
      componentName: String(dataset.trackComponent || '').slice(0, 80),
      businessId: hashBusinessId(dataset.trackBusinessId || '')
    }
  }, { trackId: '', text: '', type: '', tag: '', componentName: '', businessId: '' })
}

function safeElementText(element) {
  return silently(() => {
    const dataset = element.dataset || {}
    const explicit = dataset.trackText || element.getAttribute('aria-label') || element.getAttribute('title') || element.getAttribute('alt')
    if (explicit) return scrubText(explicit)
    const classes = elementClass(element).toLowerCase()
    if (/identity|mobile-user|user-info|order-card__identity|order-h5-row/.test(classes)) return genericLabel(classes)
    const tag = String(element.tagName || '').toLowerCase()
    if (tag === 'button' || tag === 'a' || TRACKABLE_ROLES.indexOf(String(element.getAttribute('role') || '').toLowerCase()) >= 0) {
      return scrubText(element.innerText || element.textContent || '')
    }
    return genericLabel(classes)
  }, '')
}

function scrubText(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 80)
  if (!text) return ''
  if (/\d{6,}|@|https?:\/\//i.test(text)) return '受保护操作'
  return text
}

function genericLabel(classes) {
  if (/download/.test(classes)) return '下载'
  if (/update/.test(classes)) return '修改'
  if (/delete|no-1/.test(classes)) return '删除或操作'
  if (/back/.test(classes)) return '返回'
  if (/payment/.test(classes)) return '支付'
  if (/recharge|success/.test(classes)) return '充值'
  if (/select-frame/.test(classes)) return '选择套餐'
  if (/identity/.test(classes)) return '身份菜单'
  if (/mobile-user|user-info/.test(classes)) return '用户菜单'
  return ''
}

function buildElementId(element) {
  const explicit = element && element.dataset && element.dataset.trackId
  if (explicit) return explicit
  const signature = [
    safeElementText(element),
    elementClass(element),
    domPosition(element)
  ].join('|')
  return `${pageInfo(currentRoute).routePath}:${String(element.tagName || '').toLowerCase()}:${hash(signature).slice(0, 12)}`
}

function pageInfo(route = {}) {
  return silently(() => {
    const routePath = route && route.path || window.location.pathname || '/'
    return {
      routePath: String(routePath).slice(0, 240),
      routeName: route && route.name ? String(route.name).slice(0, 80) : '',
      title: String(route && route.meta && route.meta.title || document.title || '').slice(0, 120),
      urlHost: String(window.location.host || '').slice(0, 160),
      urlPath: String(routePath).slice(0, 240),
      urlQueryHash: hash(String(window.location.search || '')),
      referrerHost: safeUrlPart(document.referrer, 'host'),
      referrerPath: safeUrlPart(document.referrer, 'pathname')
    }
  }, {})
}

function sanitizeProperties(input) {
  const output = {}
  silently(() => {
    Object.keys(input || {}).forEach(key => {
      if (SENSITIVE_KEY_PATTERN.test(key)) return
      if (!SAFE_PROPERTY_KEY_PATTERN.test(key) && key !== 'trackSource' && key !== 'interactionTrigger') return
      const value = input[key]
      if (value == null) return
      if (typeof value === 'boolean' || typeof value === 'number') {
        output[key] = value
      } else {
        output[key] = scrubText(String(value)).slice(0, 120)
      }
    })
  })
  return output
}

function post(path, payload) {
  silently(() => sendJson(
    `${trimEnd(options.endpoint || DEFAULT_ENDPOINT)}${path}`,
    JSON.stringify(payload),
    { keepalive: true }
  ))
}

function sendJson(url, payload, requestOptions = {}) {
  const authToken = getAuthToken()
  const started = silently(() => {
    if (typeof window.fetch === 'function') {
      const request = window.fetch(url, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: payload,
        keepalive: Boolean(requestOptions.keepalive),
        credentials: 'include'
      })
      if (request && typeof request.then === 'function') {
        request.then(response => {
          if (!response.ok && requestOptions.onFailure) requestOptions.onFailure()
        }).catch(() => {
          if (requestOptions.onFailure) requestOptions.onFailure()
        })
        return true
      }
    }
    if (typeof window.XMLHttpRequest === 'undefined') {
      if (requestOptions.onFailure) requestOptions.onFailure()
      return true
    }
    const xhr = new window.XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.withCredentials = true
    xhr.timeout = 5000
    xhr.setRequestHeader('Content-Type', 'application/json')
    if (authToken) xhr.setRequestHeader('Authorization', authToken)
    xhr.onload = () => {
      if ((xhr.status < 200 || xhr.status >= 300) && requestOptions.onFailure) requestOptions.onFailure()
    }
    xhr.onerror = xhr.ontimeout = () => {
      if (requestOptions.onFailure) requestOptions.onFailure()
    }
    xhr.send(payload)
    return true
  }, false)
  if (!started && requestOptions.onFailure) requestOptions.onFailure()
}

function flushInteraction() {
  if (options.flushOnInteraction === false) return
  flush(false)
}

function shouldSkipInteraction(signature) {
  const fullSignature = `${pageInfo(currentRoute).routePath}|${signature || ''}`
  const now = Date.now()
  if (fullSignature === lastInteractionSignature && now - lastInteractionTime < INTERACTION_DEDUPE_MS) return true
  lastInteractionSignature = fullSignature
  lastInteractionTime = now
  return false
}

function ensureVisitorId() {
  let id = sharedCookieGet(VISITOR_KEY) || safeLocalGet(VISITOR_KEY) || inMemoryVisitorId
  if (!id) {
    id = uuid()
  }
  inMemoryVisitorId = id
  safeLocalSet(VISITOR_KEY, id)
  sharedCookieSet(VISITOR_KEY, id)
  return id
}

function ensureSessionId() {
  let id = safeSessionGet(SESSION_KEY) || inMemorySessionId
  if (!id) {
    id = uuid()
    inMemorySessionId = id
    safeSessionSet(SESSION_KEY, id)
  }
  return id
}

function isFormControl(element) {
  const tag = String(element && element.tagName || '').toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select'
}

function safeFieldType(element) {
  const type = String(element && (element.type || element.tagName) || '').toLowerCase()
  if (/password/.test(type)) return 'password'
  if (/email/.test(type)) return 'email'
  if (/tel/.test(type)) return 'phone'
  if (/file/.test(type)) return 'file'
  if (/checkbox|radio|select|textarea|text|number/.test(type)) return type.slice(0, 30)
  return 'input'
}

function safeApiPath(value) {
  return silently(() => new URL(value, window.location.origin).pathname, String(value || '').split('?')[0]).slice(0, 240)
}

function isTrackRequest(value) {
  return silently(() => {
    const url = new URL(value, window.location.origin)
    const endpoint = new URL(options.endpoint || DEFAULT_ENDPOINT, window.location.origin)
    return url.host === endpoint.host && url.pathname.indexOf(endpoint.pathname) === 0
  }, String(value || '').indexOf('/track/') >= 0)
}

function safeBusinessCode(value) {
  const text = String(value == null ? '' : value)
  return /^[a-z0-9_-]{1,40}$/i.test(text) ? text : ''
}

function fileExtension(value) {
  const match = String(value || '').toLowerCase().match(/\.([a-z0-9]{1,12})(?:$|\?)/)
  return match ? match[1] : ''
}

function sizeBucket(bytes) {
  if (!bytes) return '0'
  if (bytes <= 1024 * 1024) return '0-1MB'
  if (bytes <= 5 * 1024 * 1024) return '1-5MB'
  if (bytes <= 20 * 1024 * 1024) return '5-20MB'
  return '20MB+'
}

function safeUrlPart(value, key) {
  return silently(() => value ? String(new URL(value)[key] || '').slice(0, 240) : '', '')
}

function hasIgnoredClass(node) {
  const classes = elementClass(node).toLowerCase()
  return IGNORE_CLASS_KEYWORDS.some(keyword => classes.indexOf(keyword) >= 0)
}

function hasAnyClass(element, classNames) {
  return classNames.some(className => silently(() => element.classList.contains(className), false))
}

function elementClass(element) {
  return silently(() => {
    if (!element || !element.className) return ''
    const value = typeof element.className === 'string' ? element.className : element.className.baseVal
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 200)
  }, '')
}

function domPosition(element) {
  return silently(() => {
    const parts = []
    let node = element
    let depth = 0
    while (node && node !== document.body && depth < 4) {
      const tag = String(node.tagName || '').toLowerCase()
      const siblings = node.parentElement ? Array.prototype.filter.call(node.parentElement.children, child => child.tagName === node.tagName) : []
      const index = siblings.length > 1 ? siblings.indexOf(node) + 1 : 0
      parts.unshift(index > 0 ? `${tag}:${index}` : tag)
      node = node.parentElement
      depth++
    }
    return parts.join('>')
  }, '')
}

function compactObject(input) {
  const output = {}
  Object.keys(input || {}).forEach(key => {
    if (input[key] !== '' && input[key] !== undefined && input[key] !== null) output[key] = input[key]
  })
  return output
}

function unique(values) {
  return values.filter((value, index) => values.indexOf(value) === index)
}

function safeIdentifier(value, fallback) {
  const text = String(value || '').replace(/[^a-z0-9_.:-]/gi, '_').slice(0, 100)
  return text || fallback
}

function first() {
  for (let i = 0; i < arguments.length; i++) {
    if (arguments[i] !== undefined && arguments[i] !== null && arguments[i] !== '') return arguments[i]
  }
  return ''
}

function trimEnd(value) {
  return String(value || '').replace(/\/+$/, '')
}

function hash(value) {
  let result = 2166136261
  const text = String(value || '')
  for (let i = 0; i < text.length; i++) {
    result ^= text.charCodeAt(i)
    result = Math.imul(result, 16777619)
  }
  return (`00000000${(result >>> 0).toString(16)}`).slice(-8)
}

function uuid() {
  return silently(() => {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
    const bytes = new Uint8Array(16)
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes)
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
    bytes[6] = (bytes[6] & 15) | 64
    bytes[8] = (bytes[8] & 63) | 128
    const hex = Array.prototype.map.call(bytes, byte => (`0${byte.toString(16)}`).slice(-2)).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }, `fallback-${Date.now()}-${Math.random().toString(16).slice(2)}`)
}
